import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface PaymentLogosProps {
  className?: string;
  showPricingLink?: boolean;
}

export function PaymentLogos({ className, showPricingLink = false }: PaymentLogosProps) {
  return (
    <div className={cn("text-center", className)}>
      <div className="flex items-center justify-center gap-4 flex-wrap mb-3">
        {/* Apple Pay */}
        <div className="h-8 w-12 bg-white border border-gray-200 rounded flex items-center justify-center">
          <svg viewBox="0 0 384 512" className="h-4 w-auto fill-black" aria-label="Apple Pay">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
          </svg>
        </div>
        {/* Google Pay */}
        <div className="h-8 w-12 bg-white border border-gray-200 rounded flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="h-4 w-auto" aria-label="Google Pay">
            <path fill="#4285F4" d="M12.24 10.28V13.7h5.05c-.21 1.07-.78 1.97-1.66 2.58l2.68 2.08c1.56-1.44 2.46-3.56 2.46-6.08 0-.58-.05-1.14-.15-1.68h-8.38z"/>
            <path fill="#34A853" d="M6.4 13.88l-.6.46-2.12 1.65c1.35 2.67 4.11 4.51 7.32 4.51 2.21 0 4.07-.73 5.42-1.98l-2.68-2.08c-.72.48-1.64.77-2.74.77-2.11 0-3.9-1.42-4.54-3.33z"/>
            <path fill="#FBBC04" d="M3.68 8.5c-.44.87-.68 1.84-.68 2.87s.24 2 .68 2.87l2.72-2.11c-.16-.47-.24-.97-.24-1.5s.08-1.02.24-1.5L3.68 8.5z"/>
            <path fill="#EA4335" d="M11 6.63c1.19 0 2.26.41 3.1 1.21l2.32-2.32C14.97 4.12 13.11 3.5 11 3.5c-3.21 0-5.97 1.84-7.32 4.51l2.72 2.11c.64-1.91 2.43-3.49 4.6-3.49z"/>
          </svg>
        </div>
        {/* Visa */}
        <div className="h-8 w-12 bg-white border border-gray-200 rounded flex items-center justify-center">
          <svg viewBox="0 0 48 48" className="h-5 w-auto" aria-label="Visa">
            <path fill="#1A1F71" d="M17.4 32.9l2.1-13H23l-2.1 13h-3.5zm13.5-12.7c-.7-.3-1.8-.5-3.2-.5-3.5 0-6 1.9-6 4.5 0 2 1.8 3.1 3.1 3.7 1.4.7 1.9 1.1 1.9 1.7 0 .9-1.1 1.3-2.2 1.3-1.5 0-2.3-.2-3.5-.7l-.5-.2-.5 3.2c.9.4 2.5.8 4.1.8 3.7 0 6.1-1.8 6.1-4.6 0-1.5-.9-2.7-3-3.7-1.2-.6-2-1-2-1.6 0-.6.7-1.1 2.1-1.1 1.2 0 2 .3 2.7.5l.3.2.5-3.1zM38 20h-2.7c-.8 0-1.5.2-1.8 1.1l-5.2 12.4h3.7l.7-2.1h4.5l.4 2.1h3.3L38 20zm-4.4 8.5l1.4-3.8.4-1.1.3 1.1.8 3.8h-2.9zM15.5 20l-3.4 9-.4-1.9c-.7-2.3-2.8-4.8-5.1-6l3.1 11.7h3.7l5.6-13H15.5z"/>
            <path fill="#F9A533" d="M8.6 20H3l-.1.4c4.4 1.1 7.3 3.8 8.5 7.1l-1.2-6.2c-.2-.8-.8-1.2-1.6-1.3z"/>
          </svg>
        </div>
        {/* Mastercard */}
        <div className="h-8 w-12 bg-white border border-gray-200 rounded flex items-center justify-center">
          <svg viewBox="0 0 48 48" className="h-6 w-auto" aria-label="Mastercard">
            <circle cx="17" cy="24" r="10" fill="#EB001B"/>
            <circle cx="31" cy="24" r="10" fill="#F79E1B"/>
            <path fill="#FF5F00" d="M24 16.5c2.4 1.9 4 4.8 4 8s-1.6 6.1-4 8c-2.4-1.9-4-4.8-4-8s1.6-6.1 4-8z"/>
          </svg>
        </div>
      </div>
      <p className="text-muted-foreground text-sm">Secure payments powered by Stripe</p>
      
      {showPricingLink && (
        <Link
          to="/pricing"
          className="inline-block mt-4 text-accent hover:underline text-sm font-medium"
        >
          View full pricing details →
        </Link>
      )}
    </div>
  );
}
